import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/outline";
import { Alert } from "../../Components/Alert";
import {
  requestListWebinar,
  useWebinarDispatch,
  useWebinarState,
} from "../../Context";
import WebinarCard from "../../Components/WebinarCard";
import WebinarListSkeleton from "../../Components/WebinarListSkeleton";
import { Pagination } from "../../Components/Pagination";
import { EmptyList } from "../../Components/EmptyList";
import { useTranslation } from "react-i18next";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const LIMIT = 4;

const webinarTypeOptions = [
  { value: "FREE", label: "webinars.webinarTypes.free" },
  { value: "PAID", label: "webinars.webinarTypes.paid" },
];

const WebinarList = () => {
  const { t } = useTranslation();
  const dispatch = useWebinarDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { webinars, error, loading, nextTokens } = useWebinarState();
  const [page, setpage] = useState(1);

  const [selectedWebinarType, setSelectedWebinarType] =
    useState(getTypeFromQuery());

  function getTypeFromQuery() {
    const params = new URLSearchParams(location.search);
    const type = params.get("type");
    return (
      webinarTypeOptions.find((opt) => opt.value === type) ||
      webinarTypeOptions[0]
    );
  }

  // When location.search changes (e.g. browser back/forward), update selectedWebinarType
  useEffect(() => {
    setSelectedWebinarType(getTypeFromQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // When selectedWebinarType changes, update query param and reload list
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("type") !== selectedWebinarType.value) {
      params.set("type", selectedWebinarType.value);
      navigate({ search: params.toString() }, { replace: true });
    }
    onLoad(selectedWebinarType.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWebinarType]);

  const onLoad = (type) => {
    setpage(1);
    requestListWebinar(dispatch, {
      page: 1,
      limit: LIMIT,
      type: type,
    });
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListWebinar(dispatch, {
        page: nextPage,
        limit: LIMIT,
        type: selectedWebinarType.value,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListWebinar(dispatch, {
        page: prevPage,
        limit: LIMIT,
        type: selectedWebinarType.value,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListWebinar(dispatch, {
        page: selectedPage,
        limit: LIMIT,
        type: selectedWebinarType.value,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  return (
    <>
      <div className="mb-6">
        <Listbox value={selectedWebinarType} onChange={setSelectedWebinarType}>
          {({ open }) => (
            <>
              <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm bg-white sm:text-sm">
                <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                  {t(selectedWebinarType.label)}
                </span>
                <SelectorIcon className="w-5 h-5 text-gray-500" />
              </ListboxButton>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-20 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm sm:w-auto">
                  {webinarTypeOptions.map((option) => (
                    <ListboxOption
                      key={option.value}
                      className={({ focus }) =>
                        classNames(
                          focus ? "text-white bg-blue-600" : "text-gray-900",
                          "cursor-default select-none relative py-2 pl-3 pr-9",
                        )
                      }
                      value={option}
                    >
                      {({ selected, focus }) => (
                        <>
                          <div className="flex items-center justify-between">
                            <span
                              className={classNames(
                                selected ? "font-semibold" : "font-normal",
                                "ml-3 mr-2 block truncate",
                              )}
                            >
                              {t(option.label)}
                            </span>
                            {selected ? (
                              <span
                                className={classNames(
                                  focus ? "text-white" : "text-blue-600",
                                  "absolute inset-y-0 right-0 flex items-center pr-4",
                                )}
                              >
                                <CheckIcon className="h-5 w-5" />
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </>
          )}
        </Listbox>
      </div>
      <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-t-lg">
        {error && (
          <Alert
            type="error"
            message={error?.message || "Error fetching webinar list."}
            errors={error?.errors}
          />
        )}
        {loading ? (
          <WebinarListSkeleton totalItem={4} />
        ) : (
          <div className="flex flex-col gap-4">
            {webinars.length === 0 ? (
              <EmptyList title="No Webinar Available" />
            ) : (
              <div className="-mx-px grid grid-cols-1 sm:grid-cols-2 sm:mx-0 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {webinars.map((webinar) => (
                  <a
                    key={webinar.id}
                    href={`webinar/${webinar.id}`}
                    className="flex border border-gray-200 rounded-lg p-4"
                  >
                    <WebinarCard webinar={webinar} />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Pagination
        className="rounded-b-lg"
        buttonPosition="end"
        page={page}
        onPrev={onPrev}
        onNext={onNext}
        onPageChange={onPageChange}
        nextTokens={nextTokens}
      />
    </>
  );
};

export default WebinarList;
